import { create } from 'zustand';
import { callQueueApi } from '../lib/api';
import type {
  CallQueue,
  CallQueueItem,
  CallQueueSummary,
  QueueFilters,
  DispositionPayload,
} from '../types';

interface CallQueueState {
  queues: CallQueue[];
  isLoadingList: boolean;
  listError: string | null;

  currentQueue: CallQueue | null;
  currentSummary: CallQueueSummary | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  currentItem: CallQueueItem | null;
  isLoadingItem: boolean;
  itemError: string | null;

  isSaving: boolean;
  saveError: string | null;

  fetchQueues: () => Promise<void>;
  fetchQueue: (id: string) => Promise<void>;
  createQueue: (input: { name: string; filtersJson: QueueFilters }) => Promise<CallQueue>;
  renameQueue: (id: string, name: string) => Promise<void>;
  deleteQueue: (id: string) => Promise<void>;
  refreshQueue: (id: string) => Promise<number>;

  fetchNext: (id: string) => Promise<void>;
  recordDisposition: (id: string, personId: string, payload: DispositionPayload) => Promise<void>;
  skipCurrent: (id: string) => Promise<void>;
}

export const useCallQueueStore = create<CallQueueState>((set, get) => ({
  queues: [],
  isLoadingList: false,
  listError: null,

  currentQueue: null,
  currentSummary: null,
  isLoadingDetail: false,
  detailError: null,

  currentItem: null,
  isLoadingItem: false,
  itemError: null,

  isSaving: false,
  saveError: null,

  fetchQueues: async () => {
    set({ isLoadingList: true, listError: null });
    try {
      const { callQueues } = await callQueueApi.list();
      set({ queues: callQueues, isLoadingList: false });
    } catch (e: any) {
      set({ listError: e.message, isLoadingList: false });
    }
  },

  fetchQueue: async (id) => {
    set({ isLoadingDetail: true, detailError: null });
    try {
      const { callQueue, summary } = await callQueueApi.get(id);
      set({ currentQueue: callQueue, currentSummary: summary, isLoadingDetail: false });
    } catch (e: any) {
      set({ detailError: e.message, isLoadingDetail: false });
    }
  },

  createQueue: async (input) => {
    set({ isSaving: true, saveError: null });
    try {
      const { callQueue } = await callQueueApi.create(input);
      set({ isSaving: false });
      return callQueue;
    } catch (e: any) {
      set({ saveError: e.message, isSaving: false });
      throw e;
    }
  },

  renameQueue: async (id, name) => {
    await callQueueApi.update(id, { name });
    await get().fetchQueues();
  },

  deleteQueue: async (id) => {
    await callQueueApi.remove(id);
    await get().fetchQueues();
  },

  refreshQueue: async (id) => {
    const { addedCount } = await callQueueApi.refresh(id);
    await get().fetchQueue(id);
    return addedCount;
  },

  fetchNext: async (id) => {
    set({ isLoadingItem: true, itemError: null });
    try {
      const { item } = await callQueueApi.next(id);
      set({ currentItem: item, isLoadingItem: false });
    } catch (e: any) {
      set({ itemError: e.message, isLoadingItem: false });
    }
  },

  recordDisposition: async (id, personId, payload) => {
    set({ isSaving: true, saveError: null });
    try {
      await callQueueApi.disposition(id, personId, payload);
      set({ isSaving: false });
      await get().fetchNext(id);
    } catch (e: any) {
      set({ saveError: e.message, isSaving: false });
      throw e;
    }
  },

  skipCurrent: async (id) => {
    const item = get().currentItem;
    if (!item) return;
    await callQueueApi.skip(id, item.personId);
    await get().fetchNext(id);
  },
}));
